import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslations } from "@/translations";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { HeroSection } from "@/components/HeroSection";
import { JobCard } from "@/components/JobCard";
import { TeaserJobCard } from "@/components/TeaserJobCard";
import { SignUpGate } from "@/components/SignUpGate";
import { SmartJobDisplay } from "@/components/SmartJobDisplay";
import { SkeletonJobCard } from "@/components/ui/skeleton-job-card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAIMatchedJobs } from "@/hooks/useAIMatchedJobs";
import { useAuth } from "@/contexts/AuthContext";
import { InstantSearchBar } from "@/components/InstantSearchBar";
import { FilterChips } from "@/components/FilterChips";
import { FilterSidebar } from "@/components/FilterSidebar";
import { FilterDrawer } from "@/components/FilterDrawer";
import { useInstantSearch } from "@/hooks/useInstantSearch";
import { Button } from "@/components/ui/button";
import { RefreshCw, Globe, CheckCircle } from "lucide-react";
import { useInView } from "react-intersection-observer";
import { SEOHead } from "@/components/seo/SEOHead";
import { StructuredData } from "@/components/seo/StructuredData";
import { generateCanonicalUrl } from "@/utils/seoUtils";
import { SavedSearchToggle } from "@/components/SavedSearchToggle";
import { ProfileCompleteness } from "@/components/ProfileCompleteness";
import { MobileStickyBar } from "@/components/MobileStickyBar";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { useIsMobile } from "@/hooks/use-mobile";
import { toast } from "@/hooks/use-toast";
import { JobAlertButton } from "@/components/JobAlertButton";

const Jobs = () => {
  const { language } = useLanguage();
  const t = useTranslations(language);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAIMode, setIsAIMode] = useState(false);
  const { user, profile } = useAuth();
  const { jobs: aiJobs, loading: aiLoading, fetchAIMatchedJobs } = useAIMatchedJobs();
  const isMobile = useIsMobile();
  const containerRef = useRef<HTMLDivElement>(null);

  // Use instant search hook
  const {
    filters,
    updateFilters,
    removeFilter,
    clearAllFilters,
    activeFilters,
    jobs,
    isLoading,
    error,
    hasMore,
    loadMore,
    performSearch,
  } = useInstantSearch();

  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
    rootMargin: "100px",
  });

  // Load more when in view
  useEffect(() => {
    if (inView && hasMore && !isLoading && !isAIMode) {
      loadMore();
    }
  }, [inView, hasMore, isLoading, loadMore, isAIMode]);

  // Scroll to jobs section after search results load
  useEffect(() => {
    if (!isLoading && jobs.length > 0 && filters.query?.trim()) {
      const resultsHeader = document.getElementById('search-results-header');
      if (resultsHeader) {
        const headerTop = resultsHeader.offsetTop;
        const offset = 100; // Add some padding from top
        window.scrollTo({
          top: headerTop - offset,
          behavior: 'smooth'
        });
      }
    }
  }, [isLoading, jobs.length, filters.query]);

  const handleSearch = (query: string, loc: string, type: string) => {
    updateFilters({ 
      query,
      location: loc === 'all' ? undefined : loc,
      job_type: type === 'all' ? undefined : type,
    });
    setIsAIMode(false);
  };

  const handleAIMatch = async () => {
    setIsAIMode(true);
    await fetchAIMatchedJobs();
  };

  // Pull to refresh
  const handleRefresh = async () => {
    if (isAIMode) {
      await fetchAIMatchedJobs();
    } else {
      await performSearch();
    }
    toast({
      description: "Jobs refreshed successfully",
    });
  };

  const { bindToElement } = usePullToRefresh({
    onRefresh: handleRefresh,
    enabled: true,
  });

  // Save search functionality
  const handleSaveSearch = () => {
    if (!user) {
      toast({
        title: "Login Required",
        description: "Please login to save searches",
        variant: "destructive"
      });
      return;
    }
    
    toast({
      description: "Search saved successfully",
    });
  };

  // Bind pull-to-refresh to container
  useEffect(() => {
    const cleanup = bindToElement(containerRef.current);
    return cleanup;
  }, [bindToElement]);

  // SEO setup
  const pageTitle = isAIMode 
    ? "AI Job Matching - Find Your Perfect Job | GreatJobs.lk"
    : filters.query?.trim()
    ? `${filters.query} Jobs in Sri Lanka | GreatJobs.lk`
    : "Find Jobs in Sri Lanka | GreatJobs.lk";
    
  const pageDescription = isAIMode
    ? "Use our AI-powered job matching to find opportunities that perfectly match your skills and preferences in Sri Lanka."
    : filters.query?.trim()
    ? `Browse ${filters.query} job opportunities in Sri Lanka. ${jobs.length}+ active job listings from top employers.`
    : "Browse thousands of job opportunities across Sri Lanka. Find your dream job from top employers in technology, finance, healthcare and more.";

  const canonicalUrl = generateCanonicalUrl('/jobs');

  // Breadcrumb data for structured data
  const breadcrumbData = [
    { name: "Home", url: "https://greatjobs.lk/" },
    { name: "Jobs", url: "https://greatjobs.lk/jobs" }
  ];

  return (
    <div className="min-h-screen bg-background" ref={containerRef}>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        canonical={canonicalUrl}
        keywords={[
          'jobs sri lanka',
          'sri lanka jobs',
          'career opportunities sri lanka',
          'job vacancies sri lanka',
          'employment sri lanka',
          ...(filters.query ? [filters.query, `${filters.query} jobs`] : [])
        ]}
      />
      
      <StructuredData type="BreadcrumbList" data={breadcrumbData} />
      <StructuredData type="WebSite" data={{}} />

      <div className="safe-area-pt">
        <Header />
      </div>
      <main className={isMobile ? "pb-20" : ""}>
        <HeroSection 
          onSearch={handleSearch}
          onAIMatch={handleAIMatch}
          initialQuery={filters.query || ''}
          initialLocation={filters.location || ''}
          initialJobType={filters.job_type || ''}
        />
        
        <div className="container mx-auto safe-area-px py-12">
          <div className="flex gap-8">
          {/* Desktop Filter Sidebar */}
          {!isAIMode && !isMobile && (
            <FilterSidebar
              filters={filters}
              onFiltersChange={updateFilters}
              onRemoveFilter={removeFilter}
              onClearAll={clearAllFilters}
              activeFilters={activeFilters}
            />
          )}

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Breadcrumbs */}
            <div className="mb-6">
              <nav aria-label="Breadcrumb">
                <ol className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <li><a href="/" className="hover:text-foreground">Home</a></li>
                  <li>/</li>
                  <li className="text-foreground font-medium">Jobs</li>
                </ol>
              </nav>
            </div>

            {/* Profile Completeness for logged in job seekers - show on homepage when no search */}
            {user && profile?.user_type === 'job_seeker' && !filters.query?.trim() && (
              <div className="mb-8">
                <ProfileCompleteness />
              </div>
            )}

            {/* Mobile Filter Drawer & Search */}
            {!isAIMode && isMobile && (
              <div className="bg-card rounded-lg shadow-lg p-4 mb-6">
                <div className="flex gap-2 mb-4">
                  <div className="flex-1">
                    <InstantSearchBar
                      filters={filters}
                      onFiltersChange={updateFilters}
                      isLoading={isLoading}
                    />
                  </div>
                  <FilterDrawer
                    filters={filters}
                    onFiltersChange={updateFilters}
                    onRemoveFilter={removeFilter}
                    onClearAll={clearAllFilters}
                    activeFilters={activeFilters}
                  />
                </div>
                
                <FilterChips
                  activeFilters={activeFilters}
                  onRemoveFilter={removeFilter}
                  onClearAll={clearAllFilters}
                />
              </div>
            )}

            {/* Desktop Search Section - only show when not in AI mode and not mobile */}
            {!isAIMode && !isMobile && (
              <div className="bg-card rounded-lg shadow-lg p-6 mb-8">
                <InstantSearchBar
                  filters={filters}
                  onFiltersChange={updateFilters}
                  isLoading={isLoading}
                />
                
                <FilterChips
                  activeFilters={activeFilters}
                  onRemoveFilter={removeFilter}
                  onClearAll={clearAllFilters}
                />
              </div>
            )}

            {/* Saved Search Toggle - show when there are search results */}
            {!isAIMode && filters.query?.trim() && jobs.length > 0 && (
              <div className="mb-6">
                <SavedSearchToggle
                  searchQuery={filters.query || ''}
                  filters={filters}
                  resultCount={jobs.length}
                />
              </div>
            )}

            {/* Error State */}
            {error && !isAIMode && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6 text-center">
                <p className="text-destructive mb-3">{error}</p>
                <Button onClick={performSearch} variant="outline" size="sm">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </Button>
              </div>
            )}

            {isAIMode ? (
              <>
                <div id="search-results-header" className="mb-6">
                  <h2 className="text-2xl font-bold mb-2">
                    {aiLoading ? 'Finding your AI matched jobs...' : `${aiJobs.length} AI matched job${aiJobs.length === 1 ? '' : 's'} found`}
                  </h2>
                  <p className="text-muted-foreground">
                    Jobs tailored to your profile and preferences
                  </p>
                </div>
                
                <div id="jobs-results" className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {aiJobs.map((job) => (
                    <JobCard 
                      key={job.id} 
                      job={{
                        ...job,
                        company: job.company
                      }} 
                    />
                  ))}
                </div>
                
                {aiJobs.length === 0 && !aiLoading && (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">
                      No matching jobs found. Try updating your profile with more skills and preferences.
                    </p>
                  </div>
                )}
                
                {aiLoading && (
                  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {[...Array(8)].map((_, i) => (
                      <SkeletonJobCard key={i} />
                    ))}
                  </div>
                )}
              </>
            ) : !filters.query?.trim() ? (
              <div className="text-center py-20">
                <div className="max-w-md mx-auto">
                  <h2 className="text-2xl font-bold mb-4">Search for Jobs</h2>
                  <p className="text-muted-foreground mb-6">
                    Enter a job title or keywords in the search box above to find relevant opportunities.
                  </p>
                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">
                      Try searching for: "Software Engineer", "Marketing Manager", "Designer", etc.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div id="search-results-header" className="mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <h2 className="text-2xl font-bold">
                      {isLoading && jobs.length === 0 ? 'Searching...' : 
                       jobs.length > 0 ? `${jobs.length}${hasMore ? '+' : ''} job${jobs.length === 1 ? '' : 's'} found` : 
                       'No jobs found'}
                    </h2>
                    {activeFilters.length > 0 && (
                      <p className="text-sm text-muted-foreground">
                        {activeFilters.length} filter{activeFilters.length !== 1 ? 's' : ''} applied
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 text-sm text-muted-foreground mb-4">
                    {filters.query && <span>Search: "{filters.query}"</span>}
                    {filters.location && filters.location !== 'all' && <span>Location: {filters.location}</span>}
                    {filters.job_type && filters.job_type !== 'all' && <span>Type: {filters.job_type}</span>}
                  </div>
                  
                  {/* Job Alert Button */}
                  {jobs.length > 0 && (
                    <div className="mb-4">
                      <JobAlertButton
                        searchQuery={filters.query || ''}
                        filters={{
                          location: filters.location !== 'all' ? filters.location : undefined,
                          job_type: filters.job_type !== 'all' ? filters.job_type : undefined,
                          experience_level: filters.experience_level !== 'all' ? filters.experience_level : undefined,
                          is_remote: filters.is_remote,
                          salary_min: filters.salary_min
                        }}
                        jobCount={!isLoading ? jobs.length : 0}
                      />
                    </div>
                  )}
                </div>
                
                <div id="jobs-results" className="space-y-4">
                  {/* Skeleton loading for initial load */}
                  {isLoading && jobs.length === 0 && (
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                      {Array.from({ length: 8 }).map((_, index) => (
                        <SkeletonJobCard key={index} />
                      ))}
                    </div>
                  )}

                  {/* Job listings with smart display */}
                  {jobs.length > 0 && (
                    <SmartJobDisplay
                      jobs={jobs.map((job) => ({
                        id: job.id,
                        title: job.title,
                        location: job.location,
                        job_type: job.job_type,
                        experience_level: job.experience_level,
                        salary_min: job.salary_min,
                        salary_max: job.salary_max,
                        currency: job.currency,
                        is_remote: job.is_remote,
                        featured: job.featured,
                        created_at: job.created_at,
                        company: {
                          name: job.company_name,
                          logo_url: job.company_logo_url
                        }
                      }))}
                      isSearch={true}
                      maxFreeViews={3}
                      showSignUpPrompt={true}
                    />
                  )}

                  {/* Load more trigger */}
                  {hasMore && jobs.length > 0 && (
                    <div ref={loadMoreRef} className="flex justify-center py-8">
                      {isLoading ? (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                          Loading more jobs...
                        </div>
                      ) : (
                        <Button onClick={loadMore} variant="outline">
                          Load More Jobs
                        </Button>
                      )}
                    </div>
                  )}

                  {/* No more results */}
                  {!hasMore && jobs.length > 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <p>You've reached the end of the job listings.</p>
                      <p className="text-sm mt-1">Try adjusting your filters to see more results.</p>
                    </div>
                  )}
                </div>

                {/* No results */}
                {jobs.length === 0 && !isLoading && (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground mb-4">
                      No jobs match your search criteria. Try different keywords or broaden your search.
                    </p>
                    {activeFilters.length > 0 && (
                      <Button onClick={clearAllFilters} variant="outline">
                        Clear All Filters
                      </Button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        </div>
      </main>
      
      {/* Mobile Sticky Bar */}
      {!isAIMode && (
        <MobileStickyBar
          filters={filters}
          onFiltersChange={updateFilters}
          onRemoveFilter={removeFilter}
          onClearAll={clearAllFilters}
          activeFilters={activeFilters}
          isLoading={isLoading}
          onSaveSearch={handleSaveSearch}
        />
      )}
      
      <div className="safe-area-pb">
        <Footer />
      </div>
    </div>
  );
};

export default Jobs;